import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { lmsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Loader2, Pencil, Trash2, Eye, GraduationCap, ImageIcon, Upload } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function MediaUpload({ bucket, value, onChange, label, accept }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await lmsApi.upload(bucket, file);
      onChange(`${BACKEND}${data.url}`);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(`${label} upload failed`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-xs hover:bg-[#F8FAFC] cursor-pointer" data-testid={`upload-${bucket}-btn`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : `Upload ${label}`}
          <input type="file" accept={accept} className="hidden" onChange={handleFile} disabled={uploading} />
        </label>
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="…or paste URL" className="h-8 text-xs flex-1 min-w-[200px]" data-testid={`url-${bucket}-input`} />
      </div>
      {value && (bucket === 'thumbnails' || bucket === 'banners' || bucket === 'images') && (
        <img src={value} alt="" className="max-h-32 rounded-lg border border-[#E2E8F0]" />
      )}
    </div>
  );
}

function LessonEditor({ courseId, lesson, onSaved, onCancel }) {
  const [data, setData] = useState({
    title: lesson?.title || '',
    lesson_type: lesson?.lesson_type || 'video',
    video_source: lesson?.video_source || 'embed',
    video_url: lesson?.video_url || '',
    duration_seconds: lesson?.duration_seconds || 0,
    content_html: lesson?.content_html || '',
    quiz: lesson?.quiz || { questions: [] },
    order: lesson?.order || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const { data: r } = await lmsApi.upload('videos', file);
      setData((d) => ({ ...d, video_source: 'file', video_url: `${BACKEND}${r.url}` }));
      toast.success('Video uploaded');
    } catch (err) {
      toast.error('Video upload failed');
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (lesson?.id) await lmsApi.updateLesson(lesson.id, data);
      else await lmsApi.createLesson(courseId, data);
      toast.success('Lesson saved');
      onSaved();
    } catch (err) {
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="lesson-editor">
      <Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} placeholder="Lesson title" data-testid="lesson-title-input" />
      <Select value={data.lesson_type} onValueChange={(v) => setData({ ...data, lesson_type: v })}>
        <SelectTrigger data-testid="lesson-type-trigger"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="video">Video</SelectItem>
          <SelectItem value="article">Article / reading</SelectItem>
          <SelectItem value="quiz">Quiz</SelectItem>
        </SelectContent>
      </Select>

      {data.lesson_type === 'video' && (
        <div className="space-y-2 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
          <div className="flex gap-2">
            <Button size="sm" variant={data.video_source === 'embed' ? 'default' : 'outline'} onClick={() => setData({ ...data, video_source: 'embed' })} data-testid="video-mode-embed">Embed URL</Button>
            <Button size="sm" variant={data.video_source === 'file' ? 'default' : 'outline'} onClick={() => setData({ ...data, video_source: 'file' })} data-testid="video-mode-file">Upload file</Button>
          </div>
          {data.video_source === 'embed' ? (
            <Input value={data.video_url} onChange={(e) => setData({ ...data, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid="video-url-input" />
          ) : (
            <div className="space-y-2">
              <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-xs hover:bg-[#F8FAFC] cursor-pointer">
                <Upload className="w-3.5 h-3.5" />Upload video file
                <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} data-testid="video-file-input" />
              </label>
              {data.video_url && <video src={data.video_url} controls className="max-h-48 rounded-lg" />}
            </div>
          )}
          <Input type="number" value={data.duration_seconds} onChange={(e) => setData({ ...data, duration_seconds: parseInt(e.target.value) || 0 })} placeholder="Duration in seconds" className="h-8 text-xs" data-testid="lesson-duration-input" />
        </div>
      )}

      {data.lesson_type === 'article' && (
        <Textarea value={data.content_html} onChange={(e) => setData({ ...data, content_html: e.target.value })} rows={8} placeholder="<p>Lesson content (HTML supported)</p>" data-testid="lesson-content-input" />
      )}

      {data.lesson_type === 'quiz' && (
        <div className="text-xs text-[#64748B] p-3 border border-dashed border-[#E2E8F0] rounded-lg">Quiz authoring is being rolled out — for now, save as Article and embed quiz links.</div>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} data-testid="lesson-cancel-btn">Cancel</Button>
        <Button onClick={save} disabled={saving || !data.title.trim()} className="careiq-btn-primary" data-testid="lesson-save-btn">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save lesson
        </Button>
      </div>
    </div>
  );
}

export default function LMSAdminPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showLessonsFor, setShowLessonsFor] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await lmsApi.listCourses();
      setCourses(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreateOrUpdate = async (formData) => {
    try {
      if (editingCourse?.id) await lmsApi.updateCourse(editingCourse.id, formData);
      else await lmsApi.createCourse(formData);
      toast.success(editingCourse?.id ? 'Course updated' : 'Course created');
      setEditingCourse(null); setShowCreate(false);
      await load();
    } catch (err) { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this course and its lessons? This cannot be undone.')) return;
    try { await lmsApi.deleteCourse(id); toast.success('Course deleted'); await load(); }
    catch (err) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="lms-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <GraduationCap className="w-6 h-6 text-[#0E7490]" />Courses
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Build training and certification programmes for your team.</p>
        </div>
        <Button className="careiq-btn-primary" onClick={() => { setEditingCourse({ title: '', description: '', status: 'draft', category: 'General' }); setShowCreate(true); }} data-testid="new-course-btn">
          <Plus className="w-4 h-4 mr-1" />New Course
        </Button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="courses-grid">
          {courses.map(c => (
            <Card key={c.id} className="careiq-card overflow-hidden" data-testid={`course-card-${c.id}`}>
              {c.thumbnail_url ? (
                <div className="w-full h-36 bg-[#F1F5F9] overflow-hidden">
                  <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-full h-36 bg-gradient-to-br from-[#0E7490]/10 to-[#0A2342]/10 flex items-center justify-center">
                  <ImageIcon className="w-10 h-10 text-[#94A3B8]" />
                </div>
              )}
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0A2342] truncate">{c.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={c.status === 'published' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#FEF3C7] text-[#92400E]'}>{c.status}</Badge>
                      <span className="text-xs text-[#64748B]">{c.lesson_count} lesson{c.lesson_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-[#64748B] line-clamp-2">{c.description}</p>
                <div className="flex gap-1 pt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => { setEditingCourse(c); setShowCreate(true); }} data-testid={`edit-course-${c.id}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowLessonsFor(c)} data-testid={`lessons-course-${c.id}`}>Lessons</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/learn/courses/${c.id}`)} data-testid={`preview-course-${c.id}`}><Eye className="w-3 h-3 mr-1" />Preview</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(c.id)} className="text-[#991B1B]" data-testid={`delete-course-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {courses.length === 0 && (
            <Card className="careiq-card col-span-full">
              <CardContent className="p-10 text-center text-[#64748B]">No courses yet — click "New Course" to start building.</CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Course create/edit dialog */}
      <Dialog open={showCreate} onOpenChange={(v) => { if (!v) { setShowCreate(false); setEditingCourse(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="course-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingCourse?.id ? 'Edit course' : 'New course'}</DialogTitle>
            <DialogDescription>Set the basics. You can add lessons after saving.</DialogDescription>
          </DialogHeader>
          {editingCourse && (
            <div className="space-y-3">
              <Input value={editingCourse.title || ''} onChange={(e) => setEditingCourse({ ...editingCourse, title: e.target.value })} placeholder="Course title" data-testid="course-title-input" />
              <Textarea value={editingCourse.description || ''} onChange={(e) => setEditingCourse({ ...editingCourse, description: e.target.value })} rows={3} placeholder="Short description" data-testid="course-desc-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input value={editingCourse.category || ''} onChange={(e) => setEditingCourse({ ...editingCourse, category: e.target.value })} placeholder="Category (e.g. Compliance)" data-testid="course-cat-input" />
                <Select value={editingCourse.status} onValueChange={(v) => setEditingCourse({ ...editingCourse, status: v })}>
                  <SelectTrigger data-testid="course-status-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-medium text-[#0A2342] mb-1">Thumbnail (card image)</div>
                <MediaUpload bucket="thumbnails" value={editingCourse.thumbnail_url} onChange={(v) => setEditingCourse({ ...editingCourse, thumbnail_url: v })} label="thumbnail" accept="image/*" />
              </div>
              <div>
                <div className="text-xs font-medium text-[#0A2342] mb-1">Featured banner (course header)</div>
                <MediaUpload bucket="banners" value={editingCourse.banner_url} onChange={(v) => setEditingCourse({ ...editingCourse, banner_url: v })} label="banner" accept="image/*" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditingCourse(null); }} data-testid="course-cancel-btn">Cancel</Button>
            <Button className="careiq-btn-primary" onClick={() => onCreateOrUpdate(editingCourse)} disabled={!editingCourse?.title?.trim()} data-testid="course-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lessons drawer */}
      <Dialog open={!!showLessonsFor} onOpenChange={(v) => { if (!v) { setShowLessonsFor(null); setEditingLesson(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="lessons-drawer">
          <DialogHeader>
            <DialogTitle>Lessons — {showLessonsFor?.title}</DialogTitle>
            <DialogDescription>Order, edit, and add lessons. Saved progress is preserved.</DialogDescription>
          </DialogHeader>
          {showLessonsFor && (
            <LessonsPanel
              courseId={showLessonsFor.id}
              editingLesson={editingLesson}
              setEditingLesson={setEditingLesson}
              onChanged={async () => {
                await load();
                const fresh = (await lmsApi.getCourse(showLessonsFor.id)).data;
                setShowLessonsFor(fresh);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LessonsPanel({ courseId, editingLesson, setEditingLesson, onChanged }) {
  const [course, setCourse] = useState(null);
  useEffect(() => {
    lmsApi.getCourse(courseId).then(({ data }) => setCourse(data));
  }, [courseId]);

  const reload = async () => {
    const { data } = await lmsApi.getCourse(courseId);
    setCourse(data);
    onChanged?.();
  };

  if (!course) return <Loader2 className="w-5 h-5 animate-spin" />;

  const lessons = course.lessons || [];
  return (
    <div className="space-y-3">
      {!editingLesson && (
        <Button size="sm" className="careiq-btn-primary" onClick={() => setEditingLesson({})} data-testid="add-lesson-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />Add lesson
        </Button>
      )}
      {editingLesson ? (
        <LessonEditor courseId={courseId} lesson={editingLesson.id ? editingLesson : null}
          onSaved={async () => { setEditingLesson(null); await reload(); }}
          onCancel={() => setEditingLesson(null)} />
      ) : (
        <div className="space-y-2">
          {lessons.map((l, i) => (
            <div key={l.id} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg" data-testid={`lesson-row-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#64748B]">Lesson {i + 1} · {l.lesson_type}</div>
                <div className="font-medium text-[#0A2342] truncate">{l.title}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setEditingLesson(l)} data-testid={`edit-lesson-${l.id}`}><Pencil className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={async () => {
                  if (!window.confirm('Delete lesson?')) return;
                  await lmsApi.deleteLesson(l.id); toast.success('Deleted'); await reload();
                }} data-testid={`delete-lesson-${l.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
          {lessons.length === 0 && <div className="p-6 text-center text-sm text-[#64748B] border border-dashed border-[#E2E8F0] rounded-lg">No lessons yet.</div>}
        </div>
      )}
    </div>
  );
}
